import { memo, Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from '@/shared/components/ui/custom/responsive-dialog';
import { Skeleton } from '@/shared/components/ui/skeleton';

import { MutualDuosGrid } from './mutualDuosGrid';

export interface MutualDuosModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  mutualCount: number;
}

export const MutualDuosModal = memo(function MutualDuosModal({
  open,
  onOpenChange,
  userId,
  mutualCount,
}: MutualDuosModalProps) {
  return (
    <ResponsiveDialog open={open} onOpenChange={onOpenChange}>
      <ResponsiveDialogContent className="no-scrollbar md:max-h-7/10 md:w-auto md:overflow-auto">
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle>
            {mutualCount} Mutual Duos
          </ResponsiveDialogTitle>
          <ResponsiveDialogDescription className="sr-only">
            List of mutual duos you share with this user
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-4 md:p-0">
          <ErrorBoundary fallback={<MutualDuosGridError />}>
            <Suspense fallback={<MutualDuosGridSkeleton />}>
              <MutualDuosGrid userId={userId} />
            </Suspense>
          </ErrorBoundary>
        </div>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
});

function MutualDuosGridSkeleton() {
  return (
    <div className="grid grid-cols-3 justify-items-center gap-x-6 gap-y-5">
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton
          key={`mutual-duo-skeleton-${i.toString()}`}
          className="h-35 w-26 rounded-2xl"
        />
      ))}
    </div>
  );
}

function MutualDuosGridError() {
  return (
    <div className="text-cold-400 flex items-center justify-center py-8 text-sm">
      Failed to load mutual duos
    </div>
  );
}
